/* ============================================================================
   COQUILLE — la shell de creación de EDISAMP
   ----------------------------------------------------------------------------
   No es la cabecera de la tienda. Aquí no hay colecciones, ni buscador, ni
   promociones, ni barra de anuncios: el cliente está creando y no hay que
   distraerlo. Lo que sí resuelve:

     identidad · volver · estado de guardado · aperçu · continuar · bolsa

   VIVE FUERA DE studio.js A PROPÓSITO. Ese fichero ya pasa de 3 000 líneas y
   cada cosa que se le añade lo hace más difícil de mover el día que el Studio
   se monte dentro del storefront. Esto sólo necesita el bus de eventos y la
   sesión: nada de aquí depende de una URL del tema.
   ========================================================================== */

const COQUILLE = (() => {
'use strict';

const $ = (s, r = document) => r.querySelector(s);

/* ---------------------------------------------------------- volver ------- */

/**
 * ¿Se puede volver a este origen?
 *
 * Un `retour` en la URL lo escribe cualquiera, así que no se navega a lo que
 * diga sin mirarlo: eso es un open redirect de manual —un enlace a EDISAMP
 * que acaba en otro sitio con la marca todavía en la cabeza del cliente—.
 *
 * Permitido, y nada más:
 *   · el mismo origen que el Studio (producción: tienda y Studio conviven)
 *   · en desarrollo, otro puerto de la misma máquina
 *   · el origen que el despliegue declare en EDISAMP_STOREFRONT_ORIGIN
 *
 * En desarrollo el puerto cambia en cada arranque, así que la lista no puede
 * ser de puertos: es de máquinas. `localhost` y `127.0.0.1` son esta máquina
 * y nada más; un `retour` a internet no entra por ahí.
 */
const LOCALES = ['localhost', '127.0.0.1', '[::1]'];

function origenPermitido(url, aqui = location) {
  let u;
  try { u = new URL(url, aqui.href); } catch (e) { return false; }
  // Un `javascript:` o un `data:` no son navegación a una tienda.
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
  if (u.origin === aqui.origin) return true;

  const declarado = (typeof window !== 'undefined' && window.EDISAMP_STOREFRONT_ORIGIN) || '';
  if (declarado && u.origin === declarado) return true;

  // Desarrollo: misma máquina, cualquier puerto. Nunca en producción.
  const enLocal = LOCALES.includes(aqui.hostname);
  return enLocal && LOCALES.includes(u.hostname);
}

/**
 * A dónde vuelve « ← ».
 *
 * No se asume la portada. El orden es: de dónde vino de verdad, luego el
 * catálogo de CUSTOM, y sólo si no hay nada, la raíz. Volver al sitio
 * equivocado es tan molesto como no poder volver.
 *
 * Antes se aceptaba cualquier `retour` que empezara por « / » y se navegaba a
 * él tal cual. En desarrollo la tienda vive en otro puerto, así que
 * `/custom/{handle}/` se resolvía contra el Studio y el cliente aterrizaba en
 * la página de error cruda de Python. Ahora se exige URL completa y se
 * comprueba el origen.
 */
function destinoRetour(aqui = location, referrer = document.referrer) {
  const params = new URLSearchParams(aqui.search);
  const explicito = params.get('retour');
  if (explicito && origenPermitido(explicito, aqui)) {
    return new URL(explicito, aqui.href).href;
  }

  try {
    const ref = referrer && new URL(referrer);
    if (ref && origenPermitido(ref.href, aqui) && !ref.pathname.startsWith('/index')) {
      return ref.href;
    }
  } catch (e) { /* referrer opaco: se sigue */ }

  /* Último recurso. Si la tienda está declarada, su catálogo CUSTOM; si no,
     el del propio Studio, que al menos existe donde estamos. */
  const declarado = (typeof window !== 'undefined' && window.EDISAMP_STOREFRONT_ORIGIN) || '';
  return declarado ? `${declarado}/custom/` : '/custom/';
}

/**
 * Volver no puede perder trabajo.
 *
 * El autosave es durable, pero « durable » no es « ya escrito »: hay un rebote
 * de 800 ms, y salir dentro de esa ventana dejaría fuera el último cambio.
 * Así que antes de irse se fuerza la escritura y se espera — poco, y con
 * techo, porque una promesa que no resuelve tampoco puede secuestrar al
 * cliente dentro del editor.
 */
async function salirCon(href, guardarAhora) {
  const marca = $('#etat-sauvegarde');
  if (typeof guardarAhora === 'function') {
    if (marca) marca.dataset.etat = 'saving';
    try {
      await Promise.race([
        Promise.resolve(guardarAhora()),
        new Promise((r) => setTimeout(r, 1200)),
      ]);
    } catch (e) { /* se informa abajo */ }
  }
  location.href = href;
}

/* ------------------------------------------------- estado de guardado ---- */

const TEXTO = {
  saving: 'Enregistrement…',
  saved: 'Enregistré',
  error: 'Problème de sauvegarde',
};

let _ocultar = null;

function pintarEstado(estado) {
  const caja = $('#etat-sauvegarde');
  if (!caja) return;
  const txt = $('.sauvegarde__txt', caja);
  if (txt) txt.textContent = TEXTO[estado] || '';
  caja.dataset.etat = estado;

  clearTimeout(_ocultar);
  /* « Enregistré » se retira solo a los pocos segundos. Un testigo permanente
     deja de leerse a los dos minutos, y entonces ya no tranquiliza a nadie.
     El error NO se retira: ese hay que verlo. */
  if (estado === 'saved') {
    _ocultar = setTimeout(() => {
      if (caja.dataset.etat === 'saved') caja.dataset.etat = 'idle';
    }, 4000);
  }
}

/* ------------------------------------------------------------ bolsa ------ */

let _cuenta = 0;

/** El rect REAL del icono. El vuelo va aquí y no a una coordenada inventada. */
function rectSac() {
  const b = $('#btn-sac');
  if (!b || b.hidden) return null;
  const r = b.getBoundingClientRect();
  return r.width ? r : null;
}

/**
 * El icono está SIEMPRE, desde que monta el editor. Lo que aparece y
 * desaparece es el contador.
 *
 * No es cosmético: MADE → BAG necesita un destino real al que ir, y un icono
 * oculto no tiene rect. Con la bolsa escondida hasta la primera pieza, el
 * primer vuelo —justo el que importa— se quedaba sin destino.
 */
function mostrarSac(n) {
  const b = $('#btn-sac');
  const c = $('#sac-compte');
  if (!b) return;
  _cuenta = n;
  b.hidden = false;
  if (c) { c.hidden = n <= 0; c.textContent = String(n); }
}

/** El acuse de recibo del icono. Una sola respuesta, no un rebote. */
function acuserSac() {
  const b = $('#btn-sac');
  if (!b) return;
  b.dataset.reagit = 'true';
  setTimeout(() => delete b.dataset.reagit, 900);
}

/* ----------------------------------------------------------- montaje ----- */

/**
 * @param {object} opts
 *   guardarAhora  () => Promise  fuerza la escritura antes de navegar
 *   alSac         () => void     qué hace el icono de la bolsa
 */
function monter(opts = {}) {
  const retour = $('#btn-retour');
  if (retour) {
    const destino = destinoRetour();
    retour.setAttribute('href', destino);
    retour.addEventListener('click', (e) => {
      e.preventDefault();
      salirCon(destino, opts.guardarAhora);
    });
  }

  // El estado lo emite la capa de persistencia; aquí sólo se pinta.
  if (typeof CREATION !== 'undefined' && CREATION.alGuardar) {
    CREATION.alGuardar((estado) => pintarEstado(estado));
  }

  const sac = $('#btn-sac');
  if (sac && typeof opts.alSac === 'function') {
    sac.addEventListener('click', opts.alSac);
  }

  return { pintarEstado, mostrarSac, acuserSac, rectSac, destinoRetour };
}

return { monter, pintarEstado, mostrarSac, acuserSac, rectSac,
         destinoRetour, origenPermitido, salirCon };
})();

if (typeof window !== 'undefined') window.COQUILLE = COQUILLE;
